import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';

import { adminApi, type AlertPerformanceResponse } from '../../services/adminApi';

const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 24, label: 'Last 24 hours' },
  { value: 168, label: 'Last 7 days' },
  { value: 720, label: 'Last 30 days' },
];

interface MetricTile {
  label: string;
  value: (data: AlertPerformanceResponse) => number;
}

const ALERT_TILES: MetricTile[] = [
  { label: 'Evaluated', value: (d) => d.alerts.evaluated },
  { label: 'Fired', value: (d) => d.alerts.fired },
  { label: 'Deduped', value: (d) => d.alerts.deduped },
  { label: 'Quiet hours', value: (d) => d.alerts.quietHours },
  { label: 'Frequency cap', value: (d) => d.alerts.frequencyCap },
  { label: 'No match', value: (d) => d.alerts.noMatch },
];

const NOTIFICATION_TILES: MetricTile[] = [
  { label: 'Notifications sent', value: (d) => d.notifications.sent },
  { label: 'Notifications total', value: (d) => d.notifications.total },
];

/**
 * Admin alert-performance metrics page (GitHub #12 area 3).
 *
 * Aggregate-only counts from the alert engine and notification dispatch:
 * alerts evaluated / fired / dedup-suppressed / quiet-hours / frequency-cap,
 * and notifications sent — over a selectable window. No end-user PII.
 *
 * Auth is enforced upstream by AdminLayout (redirects non-admins); the server
 * also re-checks admin role.
 */
export function AlertPerformancePage() {
  const [windowHours, setWindowHours] = useState(168);

  const query = useQuery({
    queryKey: ['admin', 'alert-performance', { windowHours }],
    queryFn: () => adminApi.getAlertPerformance({ windowHours }),
  });

  function onWindowChange(event: SelectChangeEvent<string>) {
    setWindowHours(Number(event.target.value));
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Alert performance
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Aggregate, non-identifying counts from the alert engine and notification dispatch.
      </Typography>

      <Paper
        sx={{ p: 2, mb: 3, display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
        elevation={0}
        variant="outlined"
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="alert-perf-window-label">Time window</InputLabel>
          <Select
            labelId="alert-perf-window-label"
            id="alert-perf-window"
            value={String(windowHours)}
            label="Time window"
            onChange={onWindowChange}
          >
            {WINDOW_OPTIONS.map((w) => (
              <MenuItem key={w.value} value={String(w.value)}>
                {w.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading alert performance" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load alert performance: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
            Alert evaluations
          </Typography>
          <Box
            component="ul"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(6, 1fr)',
              },
              gap: 2,
              listStyle: 'none',
              pl: 0,
              m: 0,
              mb: 4,
            }}
          >
            {ALERT_TILES.map((tile) => (
              <Box key={tile.label} component="li">
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{ fontSize: '1.8rem', fontWeight: 700 }}
                  >
                    {tile.value(query.data).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tile.label}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>

          <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
            Notifications
          </Typography>
          <Box
            component="ul"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)' },
              gap: 2,
              listStyle: 'none',
              pl: 0,
              m: 0,
              maxWidth: 480,
            }}
          >
            {NOTIFICATION_TILES.map((tile) => (
              <Box key={tile.label} component="li">
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{ fontSize: '1.8rem', fontWeight: 700 }}
                  >
                    {tile.value(query.data).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tile.label}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

export default AlertPerformancePage;
